import { firstValueFrom } from 'rxjs';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';
import {
  GroupCalendarDayRow,
  GroupCalendarLocationOption,
  GroupCalendarSettings,
  GroupCalendarRotationMode,
  GroupCalendarStatsLocationColumn,
  GroupCalendarTemplateOption,
  GroupCalendarStatsItem,
  GroupCalendarTemplateStatRow,
  WeeklyPlanByYearMap,
  WeeklyPlanDayMap,
  WeekRotationDayMap,
  WeekRotationWeeklyPlanByYearMap,
  WeekRotationYearConfig
} from './group-calendar-settings.interface';

const WEEKDAY_KEYS = new Set(['1', '2', '3', '4', '5', '6', '7']);

export const GROUP_CALENDAR_SETTINGS_KEY = 'group_calendar_settings';

export const LOCATIONS_PAGE_SIZE = 10000;
export const TEMPLATES_PAGE_SIZE = 1000;
export const MAX_WEEK_ROTATION_WEEKS = 26;

function parseDateKeyLocal(dateKey: string): Date {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function mod(n: number, m: number): number {
  if (m <= 0) return 0;
  return ((n % m) + m) % m;
}

function normalizeGroupIdList(raw: unknown): number[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    const ids = raw
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    return [...new Set(ids)];
  }
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return [n];
  return [];
}

function normalizeWeekRotationDayMap(raw: unknown): WeekRotationDayMap {
  const parsed = parseJsonInput<Record<string, unknown>>(raw, {});
  const result: WeekRotationDayMap = {};
  for (const key of ['1', '2', '3', '4', '5', '6', '7']) {
    const ids = normalizeGroupIdList(parsed[key]);
    if (ids.length > 0) result[key] = ids;
  }
  return result;
}

function emptyWeekRotationDayMap(): WeekRotationDayMap {
  return {};
}

export function normalizeWeekRotationWeeks(
  weeks: WeekRotationDayMap[],
  weekCount: number
): WeekRotationDayMap[] {
  const count = Math.max(1, Math.min(MAX_WEEK_ROTATION_WEEKS, Math.floor(weekCount) || 1));
  const next = weeks.slice(0, count).map((w) => ({ ...normalizeWeekRotationDayMap(w) }));
  while (next.length < count) {
    next.push(emptyWeekRotationDayMap());
  }
  return next;
}

function isValidDateKey(dateKey: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return false;
  const d = parseDateKeyLocal(dateKey);
  return !Number.isNaN(d.getTime());
}

function normalizeWeekRotationYearConfig(raw: unknown): WeekRotationYearConfig | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  const weekCount = Math.max(
    1,
    Math.min(MAX_WEEK_ROTATION_WEEKS, Math.floor(Number(obj['weekCount'])) || 1)
  );
  let anchorDate = String(obj['anchorDate'] ?? '').slice(0, 10);
  if (!isValidDateKey(anchorDate)) {
    anchorDate = formatDateKeyFromParts(new Date().getFullYear(), 0, 1);
  }
  const weeksRaw = parseJsonInput<unknown[]>(obj['weeks'], []);
  const weeks = normalizeWeekRotationWeeks(
    (Array.isArray(weeksRaw) ? weeksRaw : []).map((w) => normalizeWeekRotationDayMap(w)),
    weekCount
  );
  return { weekCount, anchorDate, weeks };
}

function normalizeWeekRotationByYear(raw: unknown): Record<string, WeekRotationYearConfig> {
  const parsed = parseJsonInput<Record<string, unknown>>(raw, {});
  const result: Record<string, WeekRotationYearConfig> = {};
  for (const [yearKey, value] of Object.entries(parsed)) {
    const cfg = normalizeWeekRotationYearConfig(value);
    if (cfg) result[yearKey] = cfg;
  }
  return result;
}

function normalizeWeekRotationWeeklyPlanByYear(raw: unknown): WeekRotationWeeklyPlanByYearMap {
  const parsed = parseJsonInput<Record<string, unknown>>(raw, {});
  const result: WeekRotationWeeklyPlanByYearMap = {};
  for (const [yearKey, yearValue] of Object.entries(parsed)) {
    if (!yearValue || typeof yearValue !== 'object' || Array.isArray(yearValue)) continue;
    const locPlans: Record<string, WeeklyPlanDayMap[]> = {};
    for (const [locationKey, planValue] of Object.entries(yearValue as Record<string, unknown>)) {
      const arr = parseJsonInput<unknown[]>(planValue, []);
      locPlans[locationKey] = (Array.isArray(arr) ? arr : []).map((w) =>
        parseJsonInput<WeeklyPlanDayMap>(w, {})
      );
    }
    result[yearKey] = locPlans;
  }
  return result;
}

function normalizeActiveRotationModeByYear(
  raw: unknown,
  groupRotationByYear: Record<string, number[]>,
  weekRotationByYear: Record<string, WeekRotationYearConfig>
): Record<string, GroupCalendarRotationMode> {
  const parsed = parseJsonInput<Record<string, unknown>>(raw, {});
  const result: Record<string, GroupCalendarRotationMode> = {};
  for (const [yearKey, value] of Object.entries(parsed)) {
    if (value === 'week' || value === 'group') {
      result[yearKey] = value;
    }
  }
  for (const yearKey of Object.keys(groupRotationByYear)) {
    if (!result[yearKey]) result[yearKey] = 'group';
  }
  for (const yearKey of Object.keys(weekRotationByYear)) {
    if (!result[yearKey]) result[yearKey] = 'week';
  }
  return result;
}

function normalizeSettingsRotationTab(raw: unknown): GroupCalendarRotationMode {
  return raw === 'week' ? 'week' : 'group';
}

export function getActiveRotationModeForYear(
  settings: GroupCalendarSettings,
  year: number
): GroupCalendarRotationMode {
  const yearKey = String(year);
  return settings.activeRotationModeByYear[yearKey] ?? 'group';
}

export function getWeekRotationConfigForYear(
  settings: GroupCalendarSettings,
  year: number
): WeekRotationYearConfig | null {
  return settings.weekRotationByYear[String(year)] ?? null;
}

export function ensureWeekRotationConfigForYear(
  weekRotationByYear: Record<string, WeekRotationYearConfig>,
  year: number
): WeekRotationYearConfig {
  const yearKey = String(year);
  if (!weekRotationByYear[yearKey]) {
    weekRotationByYear[yearKey] = {
      weekCount: 4,
      anchorDate: formatDateKeyFromParts(year, 0, 1),
      weeks: normalizeWeekRotationWeeks([], 4)
    };
  }
  return weekRotationByYear[yearKey];
}

export function ensureWeekRotationWeeklyPlanLocation(
  weekRotationWeeklyPlanByYear: WeekRotationWeeklyPlanByYearMap,
  year: number,
  locationKey: string,
  weekCount: number
): WeeklyPlanDayMap[] {
  const yearKey = String(year);
  if (!weekRotationWeeklyPlanByYear[yearKey]) {
    weekRotationWeeklyPlanByYear[yearKey] = {};
  }
  const count = Math.max(1, weekCount);
  let plans = weekRotationWeeklyPlanByYear[yearKey][locationKey];
  if (!plans) {
    plans = Array.from({ length: count }, () => ({}));
    weekRotationWeeklyPlanByYear[yearKey][locationKey] = plans;
  } else if (plans.length !== count) {
    if (plans.length > count) {
      plans = plans.slice(0, count);
    } else {
      while (plans.length < count) {
        plans.push({});
      }
    }
    weekRotationWeeklyPlanByYear[yearKey][locationKey] = plans;
  }
  return weekRotationWeeklyPlanByYear[yearKey][locationKey];
}

/** Days from anchor to dateKey (anchor day = 0). */
export function getDaysSinceAnchor(dateKey: string, anchorDate: string): number {
  const date = parseDateKeyLocal(dateKey);
  const anchor = parseDateKeyLocal(anchorDate);
  return Math.round((date.getTime() - anchor.getTime()) / 86400000);
}

export function getWeekRotationGroupIds(
  dateKey: string,
  config: WeekRotationYearConfig
): number[] {
  const weekCount = config.weekCount;
  if (weekCount <= 0 || !config.weeks.length) return [];
  const days = getDaysSinceAnchor(dateKey, config.anchorDate);
  const weekIndex = mod(Math.floor(days / 7), weekCount);
  const dayKey = getWeekdayKey(dateKey);
  const week = config.weeks[weekIndex];
  if (!week) return [];
  return normalizeGroupIdList(week[dayKey]);
}

export function getWeekRotationSlot(
  dateKey: string,
  config: WeekRotationYearConfig
): number | null {
  const ids = getWeekRotationGroupIds(dateKey, config);
  return ids.length > 0 ? ids[0] : null;
}

export function getWeekRotationWeekIndex(
  dateKey: string,
  config: WeekRotationYearConfig
): number {
  const days = getDaysSinceAnchor(dateKey, config.anchorDate);
  return mod(Math.floor(days / 7), config.weekCount);
}

export function buildDayGroupByDateFromWeekRotation(
  year: number,
  config: WeekRotationYearConfig
): Record<string, number[]> {
  const result: Record<string, number[]> = {};
  for (const dateKey of eachDateKeyInYear(year)) {
    const groupIds = getWeekRotationGroupIds(dateKey, config);
    if (groupIds.length > 0) {
      result[dateKey] = groupIds;
    }
  }
  return result;
}

export function buildDayGroupByDateFromGroupRotation(
  year: number,
  sequence: number[]
): Record<string, number[]> {
  const result: Record<string, number[]> = {};
  if (sequence.length === 0) return result;
  for (const dateKey of eachDateKeyInYear(year)) {
    const dayIndex = getDayIndexInYear(dateKey);
    const groupId = sequence[dayIndex % sequence.length];
    if (groupId != null && groupId > 0) {
      result[dateKey] = [groupId];
    }
  }
  return result;
}

export function parseJsonInput<T>(value: unknown, fallback: T): T {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as T;
}

type GroupCalendarSettingsInput = {
  [K in keyof GroupCalendarSettings]?: unknown;
};

function normalizeDayGroupByDate(raw: unknown): Record<string, number[]> {
  const parsed = parseJsonInput<Record<string, unknown>>(raw, {});
  const result: Record<string, number[]> = {};
  for (const [dateKey, val] of Object.entries(parsed)) {
    const ids = normalizeGroupIdList(val);
    if (ids.length > 0) result[dateKey] = ids;
  }
  return result;
}

function isLegacyWeeklyPlanYearEntry(value: unknown): value is WeeklyPlanDayMap {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.keys(value as Record<string, unknown>).some((key) => WEEKDAY_KEYS.has(key));
}

function normalizeWeeklyPlanByYear(raw: unknown, locationGuids: string[]): WeeklyPlanByYearMap {
  const parsed = parseJsonInput<Record<string, unknown>>(raw, {});
  const result: WeeklyPlanByYearMap = {};

  for (const [yearKey, yearValue] of Object.entries(parsed)) {
    if (!yearValue || typeof yearValue !== 'object' || Array.isArray(yearValue)) continue;

    if (isLegacyWeeklyPlanYearEntry(yearValue)) {
      const legacyPlan = parseJsonInput<WeeklyPlanDayMap>(yearValue, {});
      result[yearKey] = {};
      for (const guid of locationGuids) {
        result[yearKey][guid] = { ...legacyPlan };
      }
      result[yearKey]['other'] = { ...legacyPlan };
      continue;
    }

    const yearPlans: Record<string, WeeklyPlanDayMap> = {};
    for (const [locationKey, planValue] of Object.entries(yearValue as Record<string, unknown>)) {
      yearPlans[locationKey] = parseJsonInput<WeeklyPlanDayMap>(planValue, {});
    }
    result[yearKey] = yearPlans;
  }

  return result;
}

const DANISH_WEEKDAY_NAMES = ['', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag'];

export function getWeekdayKey(dateKey: string): string {
  const date = new Date(dateKey);
  const weekday = date.getDay() === 0 ? 7 : date.getDay();
  return String(weekday);
}

/** First letter of Danish weekday name (M, T, O, …). */
export function getWeekdayLetter(dateKey: string): string {
  const date = new Date(dateKey);
  const weekday = date.getDay() === 0 ? 7 : date.getDay();
  const name = DANISH_WEEKDAY_NAMES[weekday];
  return name ? name.charAt(0).toUpperCase() : '';
}

export function sortLocationsByName<T extends { guid: string; name: string }>(locations: T[]): T[] {
  return [...locations].sort((a, b) => a.name.localeCompare(b.name, 'da-DK'));
}

export function sortCalendarLocationGuids(
  guids: string[],
  locations: { guid: string; name: string }[]
): string[] {
  const nameByGuid = new Map(locations.map((loc) => [loc.guid, loc.name]));
  return [...guids].sort((a, b) =>
    (nameByGuid.get(a) ?? a).localeCompare(nameByGuid.get(b) ?? b, 'da-DK')
  );
}

export function buildOrderedSelectedLocations(
  selectedGuids: string[],
  allLocations: { guid: string; name: string }[]
): { guid: string; name: string }[] {
  const byGuid = new Map(allLocations.map((loc) => [loc.guid, loc]));
  return sortCalendarLocationGuids(selectedGuids, allLocations).map((guid) => {
    const loc = byGuid.get(guid);
    return { guid, name: loc?.name ?? guid.slice(0, 6) };
  });
}

export const DEFAULT_TEMPLATE_COLOR = '#94a3b8';

export function getTemplateColorFromSettings(
  settings: Pick<GroupCalendarSettings, 'templateColors'>,
  templateId: number | null | undefined
): string | null {
  if (templateId == null || templateId <= 0) return null;
  return settings.templateColors[String(templateId)] || null;
}

export function buildStatsLocationColumns(
  locationOptions: GroupCalendarLocationOption[]
): GroupCalendarStatsLocationColumn[] {
  return [
    ...locationOptions.map((loc) => ({ key: loc.guid, name: loc.name })),
    { key: 'other', name: 'Andet' }
  ];
}

export function buildTemplateLocationStatsFromApi(
  items: GroupCalendarStatsItem[],
  locationColumns: GroupCalendarStatsLocationColumn[],
  templates: GroupCalendarTemplateOption[],
  templateColors: Record<string, string>
): GroupCalendarTemplateStatRow[] {
  const templateNameById = new Map(templates.map((t) => [t.template_id, t.name]));
  const locationKeys = new Set(locationColumns.map((col) => col.key));
  const countsByTemplate = new Map<number, Record<string, { month: number; year: number }>>();

  const ensure = (templateId: number): Record<string, { month: number; year: number }> => {
    let counts = countsByTemplate.get(templateId);
    if (!counts) {
      counts = {};
      for (const col of locationColumns) {
        counts[col.key] = { month: 0, year: 0 };
      }
      countsByTemplate.set(templateId, counts);
    }
    return counts;
  };

  for (const item of items) {
    const templateId = item.template_id;
    if (templateId <= 0 || !locationKeys.has(item.location_key)) {
      continue;
    }
    const counts = ensure(templateId);
    counts[item.location_key].month += item.month_count;
    counts[item.location_key].year += item.year_count;
  }

  const rows: GroupCalendarTemplateStatRow[] = [];
  for (const [templateId, counts] of countsByTemplate) {
    const hasAny = locationColumns.some((col) => {
      const c = counts[col.key];
      return c.month > 0 || c.year > 0;
    });
    if (!hasAny) continue;

    rows.push({
      templateId,
      templateName: templateNameById.get(templateId) ?? `Skabelon ${templateId}`,
      color: templateColors[String(templateId)] ?? DEFAULT_TEMPLATE_COLOR,
      counts
    });
  }

  rows.sort((a, b) => a.templateName.localeCompare(b.templateName, 'da'));
  return rows;
}

export function getWeeklyPlanTemplateId(
  settings: GroupCalendarSettings,
  locationKey: string,
  dateKey: string,
  yearOverride?: number
): number | null {
  const year = yearOverride ?? parseInt(dateKey.slice(0, 4), 10);
  const yearKey = String(Number.isFinite(year) ? year : settings.weeklyPlanYear);
  const mode = getActiveRotationModeForYear(settings, Number(yearKey) || settings.weeklyPlanYear);

  if (mode === 'week') {
    const config = settings.weekRotationByYear[yearKey];
    if (config) {
      const weekIndex = getWeekRotationWeekIndex(dateKey, config);
      const weekPlan =
        settings.weekRotationWeeklyPlanByYear[yearKey]?.[locationKey]?.[weekIndex];
      if (weekPlan) {
        const templateId = weekPlan[getWeekdayKey(dateKey)];
        return templateId != null && templateId > 0 ? templateId : null;
      }
    }
    return null;
  }

  const plan = settings.weeklyPlanByYear[yearKey]?.[locationKey];
  if (!plan) return null;
  const templateId = plan[getWeekdayKey(dateKey)];
  return templateId != null && templateId > 0 ? templateId : null;
}

function normalizeGroupRotationByYear(raw: unknown): Record<string, number[]> {
  const parsed = parseJsonInput<Record<string, unknown>>(raw, {});
  const result: Record<string, number[]> = {};
  for (const [yearKey, value] of Object.entries(parsed)) {
    const arr = parseJsonInput<unknown[]>(value, []);
    const ids = (Array.isArray(arr) ? arr : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length > 0) {
      result[yearKey] = ids;
    }
  }
  return result;
}

export function formatDateKeyFromParts(year: number, monthIndex: number, day: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function eachDateKeyInYear(year: number): string[] {
  const keys: string[] = [];
  for (let month = 0; month < 12; month++) {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      keys.push(formatDateKeyFromParts(year, month, day));
    }
  }
  return keys;
}

export function clearDayGroupByDateForYear(
  dayGroupByDate: Record<string, number[]>,
  year: number
): void {
  const prefix = `${year}-`;
  for (const key of Object.keys(dayGroupByDate)) {
    if (key.startsWith(prefix)) {
      delete dayGroupByDate[key];
    }
  }
}

/** 0-based day index within the calendar year (Jan 1 = 0). */
export function getDayIndexInYear(dateKey: string): number {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const jan1 = new Date(y, 0, 1);
  return Math.round((date.getTime() - jan1.getTime()) / 86400000);
}

/**
 * Resolve group for a date: manual dayGroupByDate override, then yearly rotation sequence.
 */
function parseApiDateTime(value: string): Date {
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

export function formatApiDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/** Shift event start/end to a new calendar day while preserving time-of-day and duration. */
export function shiftEventDatesToDay(
  startDateStr: string,
  endDateStr: string,
  targetDateKey: string
): { start_date: string; end_date: string } {
  const start = parseApiDateTime(startDateStr);
  const end = parseApiDateTime(endDateStr);
  const [y, m, d] = targetDateKey.split('-').map(Number);
  const newStart = new Date(y, m - 1, d, start.getHours(), start.getMinutes(), start.getSeconds());
  const durationMs = Math.max(0, end.getTime() - start.getTime());
  const newEnd = new Date(newStart.getTime() + durationMs);
  return {
    start_date: formatApiDateTime(newStart),
    end_date: formatApiDateTime(newEnd)
  };
}

export function buildEventLocationPatch(locationKey: string): Record<string, unknown> {
  if (locationKey === 'other') {
    return {
      manual_location: 'Andet',
      manual_location_address: ''
    };
  }
  return {
    predefined_location: { guid: locationKey }
  };
}

export function resolveGroupIdsForDate(
  settings: GroupCalendarSettings,
  dateKey: string
): number[] {
  if (Object.prototype.hasOwnProperty.call(settings.dayGroupByDate, dateKey)) {
    return [...(settings.dayGroupByDate[dateKey] ?? [])];
  }

  const year = parseInt(dateKey.slice(0, 4), 10);
  if (!Number.isFinite(year)) return [];

  const mode = getActiveRotationModeForYear(settings, year);
  if (mode === 'week') {
    const config = settings.weekRotationByYear[String(year)];
    if (!config) return [];
    return getWeekRotationGroupIds(dateKey, config);
  }

  const rotation = settings.groupRotationByYear[String(year)];
  if (!rotation?.length) return [];

  const dayIndex = getDayIndexInYear(dateKey);
  const groupId = rotation[dayIndex % rotation.length];
  return groupId != null && groupId > 0 ? [groupId] : [];
}

export function resolveGroupIdForDate(
  settings: GroupCalendarSettings,
  dateKey: string
): number | null {
  const ids = resolveGroupIdsForDate(settings, dateKey);
  return ids.length > 0 ? ids[0] : null;
}

/** Keep the same array reference on the row when updating group ids (avoids mat-select CD loops). */
export function setRowUserGroupIds(row: GroupCalendarDayRow, ids: number[]): void {
  const unique = [...new Set(ids.filter((id) => id > 0))];
  if (!row.user_group_ids) {
    row.user_group_ids = [...unique];
  } else {
    row.user_group_ids.length = 0;
    row.user_group_ids.push(...unique);
  }
  row.user_group_id = unique[0] ?? null;
}

export function ensureWeeklyPlanLocation(
  weeklyPlanByYear: WeeklyPlanByYearMap,
  year: number,
  locationKey: string
): WeeklyPlanDayMap {
  const yearKey = String(year);
  if (!weeklyPlanByYear[yearKey]) {
    weeklyPlanByYear[yearKey] = {};
  }
  if (!weeklyPlanByYear[yearKey][locationKey]) {
    weeklyPlanByYear[yearKey][locationKey] = {};
  }
  return weeklyPlanByYear[yearKey][locationKey];
}

function normalizeSettings(raw: GroupCalendarSettingsInput, viewYear: number): GroupCalendarSettings {
  const guids = parseJsonInput<string[]>(raw.calendarLocationGuids, []);
  const calendarLocationGuids = Array.isArray(guids)
    ? guids.filter((g) => typeof g === 'string' && g.length > 0)
    : [];
  const groupRotationByYear = normalizeGroupRotationByYear(raw.groupRotationByYear);
  const weekRotationByYear = normalizeWeekRotationByYear(raw.weekRotationByYear);
  const activeRotationModeByYear = normalizeActiveRotationModeByYear(
    raw.activeRotationModeByYear,
    groupRotationByYear,
    weekRotationByYear
  );
  return {
    calendarLocationGuids,
    showGroupColumn: raw.showGroupColumn !== false,
    templateColors: parseJsonInput<Record<string, string>>(raw.templateColors, {}),
    weeklyPlanByYear: normalizeWeeklyPlanByYear(raw.weeklyPlanByYear, calendarLocationGuids),
    weeklyPlanYear: Number(raw.weeklyPlanYear) || viewYear,
    dayGroupByDate: normalizeDayGroupByDate(raw.dayGroupByDate),
    groupRotationByYear,
    groupRotationYear: Number(raw.groupRotationYear) || viewYear,
    weekRotationByYear,
    weekRotationYear: Number(raw.weekRotationYear) || viewYear,
    activeRotationModeByYear,
    settingsRotationTab: normalizeSettingsRotationTab(raw.settingsRotationTab),
    weekRotationWeeklyPlanByYear: normalizeWeekRotationWeeklyPlanByYear(
      raw.weekRotationWeeklyPlanByYear
    )
  };
}

export function readGroupCalendarSettings(
  getInput: (key: string) => unknown,
  viewYear: number
): GroupCalendarSettings {
  return normalizeSettings(
    {
      calendarLocationGuids: getInput('calendarLocationGuids'),
      showGroupColumn: getInput('showGroupColumn'),
      templateColors: getInput('templateColors'),
      weeklyPlanByYear: getInput('weeklyPlanByYear'),
      weeklyPlanYear: getInput('weeklyPlanYear'),
      groupRotationByYear: getInput('groupRotationByYear'),
      groupRotationYear: getInput('groupRotationYear'),
      weekRotationByYear: getInput('weekRotationByYear'),
      weekRotationYear: getInput('weekRotationYear'),
      activeRotationModeByYear: getInput('activeRotationModeByYear'),
      settingsRotationTab: getInput('settingsRotationTab'),
      weekRotationWeeklyPlanByYear: getInput('weekRotationWeeklyPlanByYear')
    },
    viewYear
  );
}

function resolveOrgId(dataSourceService: ComponentDataSourceService): number | null {
  const user = dataSourceService.authService.getCurrentUser() as Record<string, unknown> | null;
  if (!user) return null;
  const orgId =
    user['organizations_id'] ??
    (user['user_info'] as Record<string, unknown> | undefined)?.['organizations_id'];
  if (orgId == null) return null;
  const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId), 10);
  return Number.isFinite(n) ? n : null;
}

export async function loadGroupCalendarSettingsFromOrg(
  dataSourceService: ComponentDataSourceService,
  viewYear: number
): Promise<GroupCalendarSettings | null> {
  const orgId = resolveOrgId(dataSourceService);
  if (orgId == null) return null;
  try {
    const result = await firstValueFrom(
      dataSourceService.executeDataSource('org_settings', { organizationId: orgId })!
    );
    const raw = result?.data?.[GROUP_CALENDAR_SETTINGS_KEY];
    if (raw == null) return null;
    return normalizeSettings(parseJsonInput<GroupCalendarSettingsInput>(raw, {}), viewYear);
  } catch {
    return null;
  }
}

export async function applyGroupCalendarRotationToEvents(
  dataDestService: ComponentDataDestService,
  year: number,
  sequence: number[]
): Promise<{ updated: number }> {
  const dest = dataDestService.getDataDest('apply_group_calendar_rotation');
  if (!dest) {
    throw new Error('Gruppe-rotation er ikke tilgængelig');
  }
  const result = await firstValueFrom(dest({ year, sequence }));
  const updated = Number((result as { updated?: number })?.updated ?? 0);
  return { updated };
}

export async function applyWeekRotationToEvents(
  dataDestService: ComponentDataDestService,
  year: number,
  config: WeekRotationYearConfig
): Promise<{ updated: number }> {
  const dest = dataDestService.getDataDest('apply_group_calendar_week_rotation');
  if (!dest) {
    throw new Error('Uge-rotation er ikke tilgængelig');
  }
  const result = await firstValueFrom(
    dest({
      year,
      weekCount: config.weekCount,
      anchorDate: config.anchorDate,
      weeks: config.weeks
    })
  );
  const updated = Number((result as { updated?: number })?.updated ?? 0);
  return { updated };
}

export async function saveGroupCalendarSettingsToOrg(
  dataDestService: ComponentDataDestService,
  dataSourceService: ComponentDataSourceService,
  settings: GroupCalendarSettings
): Promise<void> {
  const orgId = resolveOrgId(dataSourceService);
  if (orgId == null) {
    throw new Error('Organisation ikke fundet');
  }
  const dest = dataDestService.getDataDest('update_org_settings');
  if (!dest) {
    throw new Error('Gem indstillinger er ikke tilgængeligt');
  }
  await firstValueFrom(
    dest({
      organizationId: orgId,
      data: { [GROUP_CALENDAR_SETTINGS_KEY]: settings }
    })
  );
}

export function extractLocationOptions(items: unknown[]): { guid: string; name: string }[] {
  const mapped = items
    .map((loc) => {
      const row = loc as { guid?: string; locations_name?: string; name?: string };
      if (!row.guid) return null;
      return {
        guid: row.guid,
        name: row.locations_name || row.name || row.guid
      };
    })
    .filter((loc): loc is { guid: string; name: string } => loc != null);
  return sortLocationsByName(mapped);
}

function normalizeTemplateRow(row: unknown): GroupCalendarTemplateOption | null {
  const t = row as Record<string, unknown>;
  const templateId = Number(t['template_id']);
  if (!Number.isFinite(templateId) || templateId <= 0) return null;

  let templateData = t['template_data'];
  if (typeof templateData === 'string') {
    try {
      templateData = JSON.parse(templateData);
    } catch {
      templateData = {};
    }
  }

  return {
    template_id: templateId,
    name: String(t['name'] ?? 'Skabelon'),
    template_data: (templateData ?? {}) as Record<string, unknown>
  };
}

/** Load event templates via the shared `templates` data source (ListResponseDTO shape). */
export async function fetchEventTemplates(
  dataSourceService: ComponentDataSourceService
): Promise<GroupCalendarTemplateOption[]> {
  try {
    const result = await firstValueFrom(
      dataSourceService.executeDataSource('templates', {
        filters: [
          { column: 'pageSize', value: TEMPLATES_PAGE_SIZE, operator: 'eq' },
          { column: 'pageIndex', value: 0, operator: 'eq' }
        ]
      })!
    );
    const items = result?.items ?? result?.data ?? [];
    return (Array.isArray(items) ? items : [])
      .map(normalizeTemplateRow)
      .filter((tpl): tpl is GroupCalendarTemplateOption => tpl != null)
      .sort((a, b) => a.name.localeCompare(b.name, 'da-DK'));
  } catch {
    return [];
  }
}
